/**
 * PWAContext
 * Context for PWA installation state and prompt visibility
 */

import { useInstallPromptScheduler, usePWAInstall } from '@shared/hooks'
import { createContext, useContext } from 'react'

interface PWAContextType {
  isInstallable: boolean
  isInstalled: boolean
  isInstalling: boolean
  isPromptVisible: boolean
  promptInstall: () => Promise<boolean>
  dismissPrompt: () => void
  resetSchedule: () => void
}

const PWAContext = createContext<PWAContextType | undefined>(undefined)

export function PWAProvider({ children }: { children: React.ReactNode }) {
  const { isInstallable, isInstalled, isInstalling, promptInstall } = usePWAInstall()
  const { shouldShowPrompt, dismissPrompt, resetSchedule } = useInstallPromptScheduler(isInstalled, isInstallable)

  return (
    <PWAContext.Provider
      value={{
        isInstallable,
        isInstalled,
        isInstalling,
        isPromptVisible: shouldShowPrompt,
        promptInstall,
        dismissPrompt,
        resetSchedule,
      }}
    >
      {children}
    </PWAContext.Provider>
  )
}

export function usePWA() {
  const context = useContext(PWAContext)
  if (!context) {
    throw new Error('usePWA must be used within PWAProvider')
  }
  return context
}

/**
 * Hook to check if PWA prompt is visible (safe to use outside provider)
 * Returns false if used outside PWAProvider
 */
export function usePWAPromptVisible(): boolean {
  const context = useContext(PWAContext)
  return context?.isPromptVisible ?? false
}
